import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { MOODS, moodByKey, moodText, moodSpark } from "@/lib/coconMoods";

export function CoconMoodCheckin({ onClose, onExplore }) {
  useTranslation();
  const T = moodText();
  const [ready, setReady] = useState(false);
  const [picked, setPicked] = useState(null);
  const [sparkTopic, setSparkTopic] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/cocon/mood/today")
      .then(({ data }) => {
        if (!alive) return;
        if (data.checked_in) onClose?.();
        else setReady(true);
      })
      .catch(() => { if (alive) onClose?.(); });
    return () => { alive = false; };
  }, [onClose]);

  const choose = async (key) => {
    if (busy) return;
    setBusy(true);
    setPicked(key);
    setSparkTopic(moodSpark(key));
    try { await api.post("/cocon/mood", { mood: key }); } catch { /* garde l'UX même si l'appel échoue */ }
    setBusy(false);
  };

  const explore = () => {
    if (onExplore && sparkTopic) onExplore(sparkTopic);
    onClose?.();
  };

  if (!ready) return null;
  const pm = picked ? moodByKey(picked) : null;

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-5" data-testid="cocon-mood-checkin">
      <div className="absolute inset-0 bg-background/75 backdrop-blur-sm" onClick={() => !picked && onClose?.()} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 14 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", damping: 24, stiffness: 260 }}
        className="relative w-full max-w-lg glass border border-primary/25 rounded-[1.75rem] p-7 sm:p-9 text-center frame-pulse"
      >
        <AnimatePresence mode="wait">
          {!picked ? (
            <motion.div key="ask" exit={{ opacity: 0 }}>
              <h2 className="font-display text-gradient-orange text-2xl sm:text-3xl mb-2">{T.title}</h2>
              <p className="text-foreground/50 text-sm mb-8">{T.subtitle}</p>
              <div className="grid grid-cols-3 gap-4">
                {MOODS.map((m) => {
                  const Icon = m.Icon;
                  return (
                    <button
                      key={m.key}
                      data-testid={`mood-option-${m.key}`}
                      onClick={() => choose(m.key)}
                      disabled={busy}
                      className="group flex flex-col items-center gap-2.5 focus:outline-none disabled:opacity-50"
                    >
                      <span
                        className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${m.ring} flex items-center justify-center transition-transform group-hover:scale-110`}
                        style={{ boxShadow: `0 0 24px -6px ${m.glow}` }}
                      >
                        <span className="w-[calc(100%-6px)] h-[calc(100%-6px)] rounded-[0.75rem] bg-background/85 flex items-center justify-center">
                          <Icon size={26} className={m.text} strokeWidth={1.6} />
                        </span>
                      </span>
                      <span className="text-xs text-foreground/60 group-hover:text-foreground transition-colors">{T.labels[m.key]}</span>
                    </button>
                  );
                })}
              </div>
              <button
                data-testid="mood-later"
                onClick={() => onClose?.()}
                className="mt-8 text-sm text-foreground/40 hover:text-primary transition-colors"
              >
                {T.later}
              </button>
            </motion.div>
          ) : (
            <motion.div key="thanks" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="py-4">
              <div className="flex justify-center mb-5">
                <span className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${pm.ring} flex items-center justify-center`} style={{ boxShadow: `0 0 30px -6px ${pm.glow}` }}>
                  <span className="w-[calc(100%-6px)] h-[calc(100%-6px)] rounded-[0.85rem] bg-background/85 flex items-center justify-center">
                    <pm.Icon size={34} className={pm.text} strokeWidth={1.5} />
                  </span>
                </span>
              </div>
              <p data-testid="mood-word" className="font-display italic text-foreground/90 text-lg leading-relaxed max-w-md mx-auto">
                {T.words[picked]}
              </p>

              {sparkTopic && (
                <div className="mt-7 flex flex-col items-center gap-4">
                  <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-primary/[0.07] border border-primary/25 text-foreground/80" data-testid="mood-spark-suggestion">
                    <Sparkles size={16} className="text-primary flex-shrink-0" />
                    <span className="text-sm">{sparkTopic}</span>
                  </div>
                  <button
                    data-testid="mood-explore-btn"
                    onClick={explore}
                    className="btn-frost-orange inline-flex items-center gap-2.5 px-7 py-3 rounded-full font-semibold"
                  >
                    {T.explore} <ArrowRight size={16} />
                  </button>
                  <button
                    data-testid="mood-close-btn"
                    onClick={() => onClose?.()}
                    className="text-sm text-foreground/40 hover:text-primary transition-colors"
                  >
                    {T.later}
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
